import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/api-client';

// Types
interface WorkOrder {
  id: string;
  code: string;
  title: string;
  description?: string;
  type: 'corrective' | 'preventive' | 'inspection' | 'installation';
  status: 'draft' | 'pending_approval' | 'approved' | 'scheduled' | 'assigned' | 'in_progress' | 'on_hold' | 'completed' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'critical';
  equipment_id: string;
  assigned_to?: string;
  scheduled_start?: string;
  scheduled_end?: string;
  actual_start?: string;
  actual_end?: string;
  labor_cost?: number;
  total_cost?: number;
  photos?: string[];
  attachments?: {
    filename: string;
    url: string;
    type: string;
    uploaded_at: string;
    uploaded_by: string;
  }[];
  created_at: string;
  updated_at: string;
}

interface WorkOrderListParams {
  status?: string;
  type?: string;
  priority?: string;
  equipmentId?: string;
  assignedTo?: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
  offset?: number;
}

interface WorkOrderListResponse {
  items: WorkOrder[];
  total: number;
}

interface WorkOrderStats {
  total: number;
  byStatus: Record<string, number>;
  byType: Record<string, number>;
  byPriority: Record<string, number>;
}

// Query keys
export const workOrderKeys = {
  all: ['work-orders'] as const,
  lists: () => [...workOrderKeys.all, 'list'] as const,
  list: (filters: WorkOrderListParams) => [...workOrderKeys.lists(), filters] as const,
  details: () => [...workOrderKeys.all, 'detail'] as const,
  detail: (id: string) => [...workOrderKeys.details(), id] as const,
  stats: () => [...workOrderKeys.all, 'stats'] as const,
};

// Hooks
export function useWorkOrderList(params: WorkOrderListParams = {}) {
  return useQuery({
    queryKey: workOrderKeys.list(params),
    queryFn: async () => {
      const searchParams = new URLSearchParams();

      if (params.status) searchParams.set('status', params.status);
      if (params.type) searchParams.set('type', params.type);
      if (params.priority) searchParams.set('priority', params.priority);
      if (params.equipmentId) searchParams.set('equipment_id', params.equipmentId);
      if (params.assignedTo) searchParams.set('assigned_to', params.assignedTo);
      if (params.search) searchParams.set('search', params.search);
      if (params.dateFrom) searchParams.set('date_from', params.dateFrom);
      if (params.dateTo) searchParams.set('date_to', params.dateTo);
      if (params.limit) searchParams.set('limit', params.limit.toString());
      if (params.offset) searchParams.set('offset', params.offset.toString());

      const response = await apiClient.get<WorkOrderListResponse>(
        `/work-orders?${searchParams.toString()}`
      );
      return response.data;
    },
  });
}

export function useWorkOrderDetail(id: string) {
  return useQuery({
    queryKey: workOrderKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get<{ workOrder: WorkOrder }>(`/work-orders/${id}`);
      return response.data.workOrder;
    },
    enabled: !!id,
  });
}

export function useWorkOrderStats() {
  return useQuery({
    queryKey: workOrderKeys.stats(),
    queryFn: async () => {
      const response = await apiClient.get<WorkOrderStats>('/work-orders/stats');
      return response.data;
    },
  });
}

export function useCreateWorkOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<WorkOrder>) => {
      const response = await apiClient.post<{ workOrder: WorkOrder }>('/work-orders', data);
      return response.data.workOrder;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workOrderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: workOrderKeys.stats() });
    },
  });
}

export function useUpdateWorkOrder(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<WorkOrder>) => {
      const response = await apiClient.put<{ workOrder: WorkOrder }>(`/work-orders/${id}`, data);
      return response.data.workOrder;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workOrderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: workOrderKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: workOrderKeys.stats() });
    },
  });
}

export function useDeleteWorkOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/work-orders/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workOrderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: workOrderKeys.stats() });
    },
  });
}

// Start a work order
export function useStartWorkOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.post<{ workOrder: WorkOrder; message: string }>(
        `/work-orders/${id}/start`
      );
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: workOrderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: workOrderKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: workOrderKeys.stats() });
    },
  });
}

// Pause a work order
export function usePauseWorkOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) => {
      const response = await apiClient.post<{ workOrder: WorkOrder; message: string }>(
        `/work-orders/${id}/pause`,
        { reason }
      );
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: workOrderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: workOrderKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: workOrderKeys.stats() });
    },
  });
}

// Complete a work order with parts and costs
interface CompleteWorkOrderData {
  work_performed?: string;
  root_cause?: string;
  solution?: string;
  parts_used?: {
    article_id: string;
    quantity: number;
    unit_cost: number;
  }[];
  labor_hours?: number;
  labor_cost?: number;
  external_cost?: number;
  completion_notes?: string;
  technician_signature?: string;
  photos?: string[];
}

interface CompleteWorkOrderResponse {
  workOrder: WorkOrder;
  stockMovements: { articleId: string; movementId: string; quantity: number }[];
  costs: {
    parts: number;
    labor: number;
    total: number;
  };
  budgetUpdated: boolean;
  message: string;
}

export function useCompleteWorkOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: CompleteWorkOrderData }) => {
      const response = await apiClient.post<CompleteWorkOrderResponse>(
        `/work-orders/${id}/complete`,
        data
      );
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: workOrderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: workOrderKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: workOrderKeys.stats() });
      // Also invalidate stock and budget queries
      queryClient.invalidateQueries({ queryKey: ['articles'] });
      queryClient.invalidateQueries({ queryKey: ['stock-movements'] });
      queryClient.invalidateQueries({ queryKey: ['budgets'] });
    },
  });
}

// Get work orders assigned to a specific technician
export function useTechnicianWorkOrders(
  technicianId: string,
  filters?: { status?: string; dateFrom?: string; dateTo?: string }
) {
  return useQuery({
    queryKey: ['work-orders', 'technician', technicianId, filters],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (filters?.status) searchParams.set('status', filters.status);
      if (filters?.dateFrom) searchParams.set('date_from', filters.dateFrom);
      if (filters?.dateTo) searchParams.set('date_to', filters.dateTo);

      const response = await apiClient.get<{ items: WorkOrder[]; total: number }>(
        `/work-orders/technician/${technicianId}?${searchParams.toString()}`
      );
      return response.data;
    },
    enabled: !!technicianId,
  });
}

// Get work orders assigned to the current user
export function useMyWorkOrders(filters?: { status?: string; dateFrom?: string; dateTo?: string }) {
  return useQuery({
    queryKey: ['work-orders', 'my-assignments', filters],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (filters?.status) searchParams.set('status', filters.status);
      if (filters?.dateFrom) searchParams.set('date_from', filters.dateFrom);
      if (filters?.dateTo) searchParams.set('date_to', filters.dateTo);

      const response = await apiClient.get<{ items: WorkOrder[]; total: number }>(
        `/work-orders/my-assignments?${searchParams.toString()}`
      );
      return response.data;
    },
  });
}

// Reserve parts for a work order
export function useReserveParts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      workOrderId,
      articleId,
      quantity,
    }: {
      workOrderId: string;
      articleId: string;
      quantity: number;
    }) => {
      const response = await apiClient.post<{ workOrder: WorkOrder }>(
        `/work-orders/${workOrderId}/parts/reserve`,
        { article_id: articleId, quantity }
      );
      return response.data.workOrder;
    },
    onSuccess: (_, { workOrderId }) => {
      queryClient.invalidateQueries({ queryKey: workOrderKeys.detail(workOrderId) });
      queryClient.invalidateQueries({ queryKey: ['articles'] });
    },
  });
}

// Unreserve parts from a work order
export function useUnreserveParts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      workOrderId,
      articleId,
    }: {
      workOrderId: string;
      articleId: string;
    }) => {
      const response = await apiClient.post<{ workOrder: WorkOrder }>(
        `/work-orders/${workOrderId}/parts/unreserve`,
        { article_id: articleId }
      );
      return response.data.workOrder;
    },
    onSuccess: (_, { workOrderId }) => {
      queryClient.invalidateQueries({ queryKey: workOrderKeys.detail(workOrderId) });
      queryClient.invalidateQueries({ queryKey: ['articles'] });
    },
  });
}
